import { CHIEF_COMPLAINT_MAX_LENGTH } from '@/src/constants'
import { useAppSelector } from '@/src/store/hooks'
import { icdsAction } from '@api'
import { useEncounter, useIcds, useIntake, useMedical } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { medicalSchema } from '@schema/pDirectory.schema'
import { FormButton, Modal, SelectInputField, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { flattenKeysAndValuesUnique, getError, showToast } from '@util'
import { Box, Form, Text } from 'grommet'
import { useEffect } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'

type Props = {
  onClose: () => void
}

const AddMedical = ({ onClose }: Props) => {
  const methods = useForm({ resolver: yupResolver(medicalSchema) })
  const status = useAppSelector(state => state.config.medical_history_transitions)
  const { AllIcds } = useIcds()
  const { patientId, encounterId, intakeId: paramsIntakeId } = useParams<string>()
  const { createMedical } = useMedical()
  const [searchParams] = useSearchParams()
  const isEncounterProblemTab = searchParams.get('encounterTab') === 'Encounter Problems'
  console.log('isEncounterProblemTab', isEncounterProblemTab)
  const { singleEncounter } = useEncounter({ id: encounterId })
  const intakeId = singleEncounter?.relationships.intake.data.id

  const { intake, updateIntake } = useIntake({ id: intakeId })

  const chiefComplaint = intake?.data.attributes.chief_complaint

  // Prefill "comment" with the Chief Complaint once the intake loads. Only for the
  // Encounter Problem flow, and only if the user hasn't already typed something in.
  useEffect(() => {
    if (isEncounterProblemTab && chiefComplaint && !methods.getValues('comment')) {
      methods.setValue('comment', chiefComplaint)
    }
  }, [chiefComplaint, isEncounterProblemTab])

  const commentValue: string = useWatch({ control: methods.control, name: 'comment' }) || ''

  const onSubmit = async (data: any) => {
    const payload: any = {
      data: {
        attributes: {
          ...data
        },
        relationships: {
          patient: {
            data: {
              id: patientId,
              type: 'patient'
            }
          },
          ...(isEncounterProblemTab && {
            encounter: {
              data: {
                id: encounterId,
                type: 'encounter'
              }
            }
          }),
          ...(intakeId && {
            intake: {
              data: {
                id: intakeId,
                type: 'intake'
              }
            }
          })
        },
        type: 'medical_history'
      }
    }

    try {
      const requests: Promise<any>[] = [createMedical(payload)]

      // Keep the Intake's Chief Complaint in sync with the Clinical Presentation text.
      // Only relevant for the Encounter Problem flow, and only if the text actually
      // changed from what's currently on the intake.
      if (isEncounterProblemTab && intakeId && data.comment && data.comment !== chiefComplaint) {
        requests.push(
          updateIntake({
            formVal: {
              data: {
                attributes: {
                  chief_complaint: data.comment
                },
                id: intakeId,
                type: 'intake'
              }
            },
            id: intakeId
          })
        )
      }

      await Promise.all(requests)
      isEncounterProblemTab
        ? showToast('Encounter Problem created successfully')
        : showToast('Medical history created successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }
  return (
    <FormProvider {...methods}>
      <Modal
        onClose={onClose}
        reset={methods.reset}
        title={isEncounterProblemTab ? 'Add Encounter Problem' : 'Add Medical'}
      >
        <Form
          onSubmit={methods.handleSubmit(onSubmit)}
          style={{ display: 'flex', flexDirection: 'column', overflow: 'auto' }}
        >
          <Box
            // flex
            overflow={'auto'}
            pad="medium"
            style={{ display: 'block', scrollbarColor: '#fff', scrollbarWidth: 'thin', width: '50em' }}
          >
            <Box gap={'24px'}>
              <Box>
                <TextAreaField
                  label={isEncounterProblemTab ? 'Clinical Presentation (Chief Complaint)' : 'Comments'}
                  maxLength={CHIEF_COMPLAINT_MAX_LENGTH}
                  name="comment"
                  placeholder="Enter Note"
                  style={{ borderRadius: '14px', minHeight: '100px' }}
                />
                {isEncounterProblemTab && (
                  <Box direction="row" justify="between" margin={{ top: 'xsmall' }}>
                    <Text color="dark-4" size="xsmall">
                      Pre-filled from Chief Complaint. Edit as needed for this problem.
                    </Text>
                    <Text color="dark-4" size="xsmall">
                      {commentValue.length}/{CHIEF_COMPLAINT_MAX_LENGTH}
                    </Text>
                  </Box>
                )}
              </Box>

              <Box>
                <Controller
                  name="icd_codings_attributes"
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <Select
                      defaultOptions={AllIcds}
                      error={error?.message}
                      getOptionLabel={({ attributes }) => `${attributes.title} - ${attributes.code}`}
                      getOptionValue={({ id }) => id}
                      isAsync
                      isMulti
                      label={isEncounterProblemTab ? 'Coding (Diagnosis / Problem Code)' : 'Coding'}
                      loadOptions={icdsAction}
                      onChange={selectedOptions => {
                        const values = selectedOptions
                          ? selectedOptions?.map((option: any) => ({
                              icd_code_id: option?.id // Map the selected value
                            }))
                          : []
                        onChange(values) // Update the form value
                      }}
                    />
                  )}
                />
                {isEncounterProblemTab && (
                  <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                    Select the most appropriate code that matches the clinical presentation.
                  </Text>
                )}
              </Box>

              <Box>
                <SelectInputField label="Status" name="status" options={flattenKeysAndValuesUnique(status || {})} />
                {isEncounterProblemTab && (
                  <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                    Select the current status of this problem.
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
          <FormButton isLoading={methods.formState.isSubmitting} onClose={onClose} submit="Save" />
        </Form>
      </Modal>
    </FormProvider>
  )
}

export default AddMedical
